/**
 * Starter Template Card Component
 * 
 * Displays a starter template in the Starter Library with preview and "Use This Template" CTA.
 * Per design.md: Premium UI with semantic tokens, lucide icons, no emojis.
 */

import { useState } from 'react';
import type { Doc } from 'convex/_generated/dataModel';
import { motion } from 'framer-motion';
import {
    Card, CardContent,
    Button, Badge,
    Sparkles, Eye, Copy, Check,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from '@uprosal/ui';

interface StarterTemplateCardProps {
    template: Doc<'prompts'>;
    index: number;
    onUseTemplate: () => void;
}

export function StarterTemplateCard({ template, index, onUseTemplate }: StarterTemplateCardProps) {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [isCloning, setIsCloning] = useState(false);

    const handleUseTemplate = async () => {
        setIsCloning(true);
        try {
            await onUseTemplate();
        } finally {
            setIsCloning(false);
        }
    };

    // Get category color
    const getCategoryColor = (category: string | undefined) => {
        switch (category?.toLowerCase()) {
            case 'enthusiasm':
                return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
            case 'questions':
                return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
            case 'proof':
                return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
            case 'experience':
                return 'bg-green-500/10 text-green-600 border-green-500/20';
            case 'confidence':
                return 'bg-red-500/10 text-red-600 border-red-500/20';
            case 'technical':
                return 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20';
            case 'speed':
                return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
            case 'general':
                return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
            default:
                return 'bg-primary/10 text-primary border-primary/20';
        }
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
            >
                <Card className="group hover:shadow-lg transition-shadow h-full flex flex-col">
                    <CardContent className="pt-6 flex flex-col flex-1">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
                                <Sparkles className="w-5 h-5 text-primary" />
                            </div>
                            {template.category && (
                                <Badge
                                    variant="outline"
                                    className={`text-xs ${getCategoryColor(template.category)}`}
                                >
                                    {template.category}
                                </Badge>
                            )}
                        </div>

                        {/* Content */}
                        <h3 className="font-semibold text-foreground mb-1 line-clamp-1" title={template.name}>
                            {template.name}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
                            {template.description || 'A proven template to help you win more proposals.'}
                        </p>

                        {/* Actions */}
                        <div className="flex gap-2 mt-auto">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => setPreviewOpen(true)}
                            >
                                <Eye className="w-4 h-4 mr-1" />
                                Preview
                            </Button>
                            <Button
                                size="sm"
                                className="flex-1"
                                onClick={handleUseTemplate}
                                disabled={isCloning}
                            >
                                {isCloning ? (
                                    <>
                                        <Check className="w-4 h-4 mr-1" />
                                        Added!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4 mr-1" />
                                        Use This
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogClose onClick={() => setPreviewOpen(false)} />

                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
                                <Sparkles className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <DialogTitle>{template.name}</DialogTitle>
                                <DialogDescription>
                                    {template.description || 'Starter template from the Upzora library'}
                                </DialogDescription>
                            </div>
                        </div>
                        {template.category && (
                            <Badge
                                variant="outline"
                                className={`w-fit mt-2 ${getCategoryColor(template.category)}`}
                            >
                                {template.category}
                            </Badge>
                        )}
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto mt-4">
                        <pre className="text-sm text-foreground whitespace-pre-wrap font-mono bg-muted/50 p-4 rounded-lg border border-border">
                            {template.template}
                        </pre>
                    </div>

                    <DialogFooter className="mt-4 gap-3">
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                            Close
                        </Button>
                        <Button onClick={() => { handleUseTemplate(); setPreviewOpen(false); }}>
                            <Copy className="w-4 h-4 mr-2" />
                            Use This Template
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
